import React, { useState, useMemo } from 'react';
import { Save, Edit2, X, Copy, Check, Megaphone } from 'lucide-react';

// Component imports
import AdPreview from './components/AdPreview';

// Platform editor imports
import GoogleAdsEditor from './platforms/GoogleAdsEditor';
import FacebookAdsEditor from './platforms/FacebookAdsEditor';
import LinkedInAdsEditor from './platforms/LinkedInAdsEditor';
import DisplayNetworkEditor from './platforms/DisplayNetworkEditor';
import CTVEditor from './platforms/CTVEditor';
import RadioAdsEditor from './platforms/RadioAdsEditor';

// Type and config imports
import { AD_PLATFORMS, AdPlatformKey } from './config/adPlatformConfig';
import { AdPlatformContent, AdsAsset } from './types/ads.types';

interface AdsPreviewProps {
  asset: AdsAsset;
  apiBaseUrl?: string;
  onSave?: (assetId: number, content: string) => Promise<void>;
}

export default function AdsPreview({ asset, apiBaseUrl = '', onSave }: AdsPreviewProps) {
  // Parse metadata
  const parseMetadata = () => {
    if (asset.metadata) {
      if (typeof asset.metadata === 'string') {
        try {
          return JSON.parse(asset.metadata);
        } catch (e) {
          return {};
        }
      }
      return asset.metadata;
    }
    return {};
  };

  const initialMetadata = parseMetadata();

  // Helper function to clean invalid blob URLs from loaded content
  const cleanInvalidBlobUrls = (content: any) => {
    if (!content) return content;

    const cleanedContent = { ...content };

    // Clean blob URLs from each platform's creatives
    Object.keys(cleanedContent).forEach((platform) => {
      const platformData = cleanedContent[platform];
      if (platformData && platformData.creatives && Array.isArray(platformData.creatives)) {
        platformData.creatives = platformData.creatives.filter((creative: any) =>
          !creative.url || !creative.url.startsWith('blob:')
        );
      }
    });

    return cleanedContent;
  };

  // Parse content - it might be JSON string with platform data
  const parseContent = () => {
    if (asset.content) {
      if (typeof asset.content === 'string') {
        try {
          const parsed = JSON.parse(asset.content);
          // Check if this is platform-specific content
          if (parsed.google || parsed.facebook || parsed.linkedin ||
              parsed.display || parsed.ctv || parsed.radio) {
            // Clean any invalid blob URLs from loaded content
            return cleanInvalidBlobUrls(parsed);
          }
        } catch (e) {
          // Not JSON, return as is
        }
      } else if (typeof asset.content === 'object') {
        // Already parsed object - clean blob URLs
        return cleanInvalidBlobUrls(asset.content);
      }
    }
    return null;
  };

  const contentData = parseContent();

  // State management
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Blob URL management at component level
  const blobUrlManagerRef = React.useRef<{
    urls: Set<string>;
    add: (url: string) => void;
    remove: (url: string) => void;
    cleanup: () => void;
  }>({
    urls: new Set(),
    add: (url: string) => {
      if (url.startsWith('blob:')) {
        blobUrlManagerRef.current.urls.add(url);
      }
    },
    remove: (url: string) => {
      if (url.startsWith('blob:') && blobUrlManagerRef.current.urls.has(url)) {
        blobUrlManagerRef.current.urls.delete(url);
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          console.warn('Failed to revoke blob URL:', e);
        }
      }
    },
    cleanup: () => {
      blobUrlManagerRef.current.urls.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore cleanup errors
        }
      });
      blobUrlManagerRef.current.urls.clear();
    }
  });

  // Cleanup blob URLs on unmount
  React.useEffect(() => {
    return () => {
      blobUrlManagerRef.current.cleanup();
    };
  }, []);

  // Initialize platform content - prefer content over metadata
  const [platformContent, setPlatformContent] = useState<AdPlatformContent>({
    google: contentData?.google || initialMetadata.google || {
      campaignName: '',
      headline: '',
      description: '',
      campaignType: 'search',
      headlines: [],
      descriptions: [],
      finalUrl: '',
      keywords: [],
      budget: 50,
      budgetType: 'daily',
      bidStrategy: 'Maximize Clicks',
      startDate: '',
      endDate: '',
      status: 'draft',
      creatives: []
    },
    facebook: contentData?.facebook || initialMetadata.facebook || {
      campaignName: '',
      headline: '',
      primaryText: '',
      objective: 'traffic',
      placement: ['facebook_feed', 'instagram_feed'],
      callToAction: 'learn_more',
      linkUrl: '',
      budget: 50,
      budgetType: 'daily',
      bidStrategy: 'Lowest Cost',
      startDate: '',
      endDate: '',
      status: 'draft',
      creatives: [],
      demographics: {
        ageMin: 18,
        ageMax: 65,
        gender: 'all',
        locations: []
      }
    },
    linkedin: contentData?.linkedin || initialMetadata.linkedin || {
      campaignName: '',
      headline: '',
      description: '',
      adFormat: 'sponsored_content',
      introText: '',
      budget: 100,
      budgetType: 'daily',
      bidStrategy: 'Maximum Delivery',
      startDate: '',
      endDate: '',
      status: 'draft',
      creatives: [],
      targetingCriteria: {}
    },
    display: contentData?.display || initialMetadata.display || {
      campaignName: '',
      headline: '',
      description: '',
      adSizes: ['300x250'],
      dsp: 'google_display',
      inventoryTypes: ['web'],
      targetingStrategy: 'contextual',
      budget: 100,
      budgetType: 'daily',
      bidStrategy: 'CPM',
      startDate: '',
      endDate: '',
      status: 'draft',
      creatives: [],
      brandSafety: {
        excludedCategories: []
      },
      viewability: 'standard',
      frequency: {
        cap: 3,
        period: 'day'
      }
    },
    ctv: contentData?.ctv || initialMetadata.ctv || {
      campaignName: '',
      headline: '',
      platforms: ['roku'],
      videoSpecs: {
        resolution: '1920x1080',
        aspectRatio: '16:9',
        duration: 30,
        format: 'mp4'
      },
      budget: 5000,
      budgetType: 'total',
      bidStrategy: 'CPM',
      startDate: '',
      endDate: '',
      status: 'draft',
      creatives: [],
      targetingOptions: {}
    },
    radio: contentData?.radio || initialMetadata.radio || {
      campaignName: '',
      headline: '',
      platform: 'spotify',
      audioSpecs: {
        duration: 30,
        format: 'mp3',
        bitrate: 256
      },
      voiceoverScript: '',
      budget: 1000,
      budgetType: 'total',
      bidStrategy: 'CPM',
      startDate: '',
      endDate: '',
      status: 'draft',
      creatives: [],
      targetingOptions: {}
    }
  });

  // Only show the platform tab relevant to the current asset
  const activePlatforms = useMemo(() => {
    const filename = asset.file_name.toLowerCase();

    // Determine which platform this asset is for
    if (filename.includes('google')) return ['google'] as AdPlatformKey[];
    if (filename.includes('facebook') || filename.includes('instagram')) return ['facebook'] as AdPlatformKey[];
    if (filename.includes('linkedin')) return ['linkedin'] as AdPlatformKey[];
    if (filename.includes('display') || filename.includes('retargeting')) return ['display'] as AdPlatformKey[];
    if (filename.includes('ctv') || filename.includes('streaming')) return ['ctv'] as AdPlatformKey[];
    if (filename.includes('spotify') || filename.includes('podcast') || filename.includes('radio')) return ['radio'] as AdPlatformKey[];

    // Fallback: check content to determine platform
    if (contentData?.google || platformContent.google?.campaignName) return ['google'] as AdPlatformKey[];
    if (contentData?.facebook || platformContent.facebook?.campaignName) return ['facebook'] as AdPlatformKey[];
    if (contentData?.linkedin || platformContent.linkedin?.campaignName) return ['linkedin'] as AdPlatformKey[];
    if (contentData?.display || platformContent.display?.campaignName) return ['display'] as AdPlatformKey[];
    if (contentData?.ctv || platformContent.ctv?.campaignName) return ['ctv'] as AdPlatformKey[];
    if (contentData?.radio || platformContent.radio?.campaignName) return ['radio'] as AdPlatformKey[];

    // Ultimate fallback
    return ['google'] as AdPlatformKey[];
  }, [asset.file_name, contentData]);

  // Auto-detect primary platform based on current asset
  const getAssetPrimaryPlatform = (): AdPlatformKey => {
    const filename = asset.file_name.toLowerCase();

    // Determine platform based on filename
    if (filename.includes('google')) return 'google';
    if (filename.includes('facebook') || filename.includes('instagram')) return 'facebook';
    if (filename.includes('linkedin')) return 'linkedin';
    if (filename.includes('display') || filename.includes('retargeting')) return 'display';
    if (filename.includes('ctv') || filename.includes('streaming')) return 'ctv';
    if (filename.includes('spotify') || filename.includes('podcast') || filename.includes('radio')) return 'radio';

    // Fallback: check which platform has data in current asset
    if (platformContent.google?.campaignName) return 'google';
    if (platformContent.facebook?.campaignName) return 'facebook';
    if (platformContent.linkedin?.campaignName) return 'linkedin';
    if (platformContent.display?.campaignName) return 'display';
    if (platformContent.ctv?.campaignName) return 'ctv';
    if (platformContent.radio?.campaignName) return 'radio';

    return 'google'; // Ultimate fallback
  };

  // Set selectedPlatform based on current asset
  const [selectedPlatform, setSelectedPlatform] = useState<AdPlatformKey>(getAssetPrimaryPlatform());

  // Update selectedPlatform and platform content when asset changes
  React.useEffect(() => {
    // Only update if we're not currently editing
    if (!isEditing) {
      setSelectedPlatform(getAssetPrimaryPlatform());

      // Reset platform content when switching assets - parse fresh data from current asset
      const newContentData = (() => {
        if (asset.content) {
          if (typeof asset.content === 'string') {
            try {
              const parsed = JSON.parse(asset.content);
              // Check if this is platform-specific content
              if (parsed.google || parsed.facebook || parsed.linkedin ||
                  parsed.display || parsed.ctv || parsed.radio) {
                return parsed;
              }
            } catch (e) {
              // Not JSON, return as is
            }
          } else if (typeof asset.content === 'object') {
            // Already parsed object
            return asset.content;
          }
        }
        return null;
      })();

      const newInitialMetadata = (() => {
        if (asset.metadata) {
          if (typeof asset.metadata === 'string') {
            try {
              return JSON.parse(asset.metadata);
            } catch (e) {
              return {};
            }
          }
          return asset.metadata;
        }
        return {};
      })();

      // Asset switching complete

      setPlatformContent({
        google: newContentData?.google || newInitialMetadata.google || {
          campaignName: '',
          headline: '',
          description: '',
          campaignType: 'search',
          headlines: [],
          descriptions: [],
          finalUrl: '',
          keywords: [],
          budget: 50,
          budgetType: 'daily',
          bidStrategy: 'Maximize Clicks',
          startDate: '',
          endDate: '',
          status: 'draft',
          creatives: []
        },
        facebook: newContentData?.facebook || newInitialMetadata.facebook || {
          campaignName: '',
          headline: '',
          primaryText: '',
          objective: 'traffic',
          placement: ['facebook_feed', 'instagram_feed'],
          callToAction: 'learn_more',
          linkUrl: '',
          budget: 50,
          budgetType: 'daily',
          bidStrategy: 'Lowest Cost',
          startDate: '',
          endDate: '',
          status: 'draft',
          creatives: [],
          demographics: {
            ageMin: 18,
            ageMax: 65,
            gender: 'all',
            locations: []
          }
        },
        linkedin: newContentData?.linkedin || newInitialMetadata.linkedin || {
          campaignName: '',
          headline: '',
          description: '',
          adFormat: 'sponsored_content',
          introText: '',
          budget: 100,
          budgetType: 'daily',
          bidStrategy: 'Maximum Delivery',
          startDate: '',
          endDate: '',
          status: 'draft',
          creatives: [],
          targetingCriteria: {}
        },
        display: newContentData?.display || newInitialMetadata.display || {
          campaignName: '',
          headline: '',
          description: '',
          adSizes: ['300x250'],
          dsp: 'google_display',
          inventoryTypes: ['web'],
          targetingStrategy: 'contextual',
          budget: 100,
          budgetType: 'daily',
          bidStrategy: 'CPM',
          startDate: '',
          endDate: '',
          status: 'draft',
          creatives: [],
          brandSafety: {
            excludedCategories: []
          },
          viewability: 'standard',
          frequency: {
            cap: 3,
            period: 'day'
          }
        },
        ctv: newContentData?.ctv || newInitialMetadata.ctv || {
          campaignName: '',
          headline: '',
          platforms: ['roku'],
          videoSpecs: {
            resolution: '1920x1080',
            aspectRatio: '16:9',
            duration: 30,
            format: 'mp4'
          },
          budget: 5000,
          budgetType: 'total',
          bidStrategy: 'CPM',
          startDate: '',
          endDate: '',
          status: 'draft',
          creatives: [],
          targetingOptions: {}
        },
        radio: newContentData?.radio || newInitialMetadata.radio || {
          campaignName: '',
          headline: '',
          platform: 'spotify',
          audioSpecs: {
            duration: 30,
            format: 'mp3',
            bitrate: 256
          },
          voiceoverScript: '',
          budget: 1000,
          budgetType: 'total',
          bidStrategy: 'CPM',
          startDate: '',
          endDate: '',
          status: 'draft',
          creatives: [],
          targetingOptions: {}
        }
      });
    }
  }, [asset.id, asset.file_name, asset.content, asset.metadata]); // Track asset changes more comprehensively

  // Update platform content
  const updatePlatformContent = (platform: AdPlatformKey, field: string, value: any) => {
    setPlatformContent(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value
      }
    }));
  };

  // Helper function to clean blob URLs from content before saving
  const cleanBlobUrlsFromContent = (content: AdPlatformContent): AdPlatformContent => {
    const cleanedContent = JSON.parse(JSON.stringify(content)); // Deep clone

    // Clean blob URLs from each platform's creatives
    Object.keys(cleanedContent).forEach((platform) => {
      const platformData = cleanedContent[platform as AdPlatformKey];
      if (platformData && platformData.creatives) {
        // Don't filter out creatives, just remove blob URLs that haven't been replaced with R2 URLs
        // Keep creatives that have been uploaded (non-blob URLs) or are external URLs
        platformData.creatives = platformData.creatives.filter(creative =>
          !creative.url.startsWith('blob:') && !creative.uploading
        );
      }
    });

    return cleanedContent;
  };

  // Handle save
  const handleSave = async () => {
    if (!onSave) return;

    setIsSaving(true);
    try {
      // Clean blob URLs before saving to prevent invalid URLs from being persisted
      const cleanedContent = cleanBlobUrlsFromContent(platformContent);
      await onSave(asset.id, JSON.stringify(cleanedContent));
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle copy
  const handleCopy = () => {
    const currentPlatformData = platformContent[selectedPlatform];
    if (currentPlatformData) {
      navigator.clipboard.writeText(JSON.stringify(currentPlatformData, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Render platform-specific editor
  const renderPlatformEditor = () => {
    const props = {
      content: platformContent[selectedPlatform],
      isEditing,
      onChange: (field: string, value: any) => updatePlatformContent(selectedPlatform, field, value),
      blobManager: blobUrlManagerRef.current,
      contentId: (asset as any).content_id || asset.id, // Use content_id if available, fallback to asset.id
      apiBaseUrl
    };

    switch (selectedPlatform) {
      case 'google':
        return <GoogleAdsEditor {...props} content={platformContent.google!} />;
      case 'facebook':
        return <FacebookAdsEditor {...props} content={platformContent.facebook!} />;
      case 'linkedin':
        return <LinkedInAdsEditor {...props} content={platformContent.linkedin!} />;
      case 'display':
        return <DisplayNetworkEditor {...props} content={platformContent.display!} />;
      case 'ctv':
        return <CTVEditor {...props} content={platformContent.ctv!} />;
      case 'radio':
        return <RadioAdsEditor {...props} content={platformContent.radio!} />;
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Megaphone className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold">Digital Ads Campaign Editor</h2>
            <span className="text-sm text-gray-500">{asset.file_name}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCopy}
              className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <Edit2 className="w-4 h-4" />
                <span className="text-sm">Edit</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex items-center space-x-1 px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
                >
                  <X className="w-4 h-4" />
                  <span className="text-sm">Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                >
                  <Save className="w-4 h-4" />
                  <span className="text-sm">{isSaving ? 'Saving...' : 'Save'}</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor Panel */}
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-4xl mx-auto">
            {renderPlatformEditor()}
          </div>
        </div>

        {/* Preview Panel */}
        <div className="w-1/2 border-l bg-white overflow-auto">
          <AdPreview
            platform={selectedPlatform}
            content={platformContent[selectedPlatform]}
            metadata={initialMetadata}
          />
        </div>
      </div>
    </div>
  );
}